<!--
@component
Bracket

Renders the knockout-stage bracket as an SVG connector layer plus an
overlay of `MatchEntry` components positioned absolutely above it. The
third-place match is rendered separately in the bottom-right corner.

Layout maths and the SVG path generator live in `./layout.ts`; the
per-match card + venue/date block is `./MatchEntry.svelte`.

This component handles the *visual* bracket only. The accessible-table
fallback is rendered alongside it from `./index.svelte`.
-->

<script lang="ts">
  import { appState } from '$lib/stores/index.svelte';
  import { scaleBand } from 'd3-scale';
  import { range } from 'd3-array';
  import { GraphicBlock } from '@reuters-graphics/graphics-components';
  import Spinner from '$lib/components/common/Spinner/index.svelte';
  import MatchEntry from './MatchEntry.svelte';
  import { drawElbow, computeMatchesLayout } from './layout';
  import type { Round } from '../content';

  let {
    note = '',
    matches,
    thirdPlaceMatch,
    venues,
    mobileBreakpoint = 900,
    heightContainer = $bindable(),
  }: {
    note?: string;
    matches: Round[];
    thirdPlaceMatch?: Round | null;
    venues: any[];
    mobileBreakpoint?: number;
    heightContainer?: number;
  } = $props();

  const numRounds = matches.length;
  // Guard against missing/empty knockout data (e.g. pre-tournament, when the
  // feed only contains group-stage fixtures or no fixtures at all). Without
  // this the SSR prerender crashes on `matches[0].matches.length`.
  const hasBracketData = numRounds > 0 && matches[0]?.matches?.length > 0;
  const numMaxMatches = hasBracketData ? matches[0].matches.length : 0;

  /// /////////////////////////
  /// /////// Dimensions //////
  /// /////////////////////////
  const CARD_HEIGHT = 140;
  const MARGINS = { top: 30, right: 15, bottom: 35, left: 15 };
  const PADDING_VERTICAL = 0.5;
  const PADDING_HORIZONTAL = 0.2;

  let widthContainer = $state<number | undefined>(undefined);

  // Set default heightContainer if not provided
  if (heightContainer === undefined) {
    heightContainer = numMaxMatches * CARD_HEIGHT;
  }

  let width = $derived(
    appState.windowWidth >= mobileBreakpoint ?
      (widthContainer ?? 0)
    : mobileBreakpoint
  );
  let height = $derived(heightContainer ?? 0);
  let boundedHeight = $derived(height - MARGINS.top - MARGINS.bottom);

  /// /////////////////////////
  /// /////// Scales //////////
  /// /////////////////////////
  let yScale = $derived(
    scaleBand<number>()
      .domain(range(0, numMaxMatches))
      .range([0, boundedHeight])
      .paddingInner(PADDING_VERTICAL)
  );
  let heightRect = $derived(yScale.bandwidth());

  let xScale = $derived(
    scaleBand<number>()
      .domain(range(0, numRounds))
      // Inset by MARGINS.left/right so the leftmost card and rightmost
      // card each get the configured gutter from the wrapper edges.
      .range([MARGINS.left, width - MARGINS.right])
      .paddingInner(PADDING_HORIZONTAL)
  );
  let widthRect = $derived(xScale.bandwidth());

  /// /////////////////////////
  /// /////// Layout //////////
  /// /////////////////////////
  let matchesLayout = $derived(
    computeMatchesLayout(matches, xScale, yScale, widthRect, heightRect)
  );

  // Position of the first match in each (non-Winner) round — used to anchor
  // the round-name labels above the bracket.
  let roundsPositions = $derived(
    matchesLayout
      .filter((d) => d.round !== 'Winner')
      .map((round) => {
        const firstMatch = round.matches[0];
        return {
          x: round.xPos + (firstMatch?.x ?? 0),
          y: round.yPos + (firstMatch?.y ?? 0),
          round: round.round,
        };
      })
  );

  // Rounds rendered with cards (skip the final "Winner" placeholder).
  let cardRounds = $derived(matchesLayout.filter((_, i) => i !== numRounds));
  // Rounds whose matches need connector paths to the next stage.
  let connectorRounds = $derived(
    matchesLayout.filter((_, i) => i !== numRounds && i !== numRounds - 1)
  );

  /// /////////////////////////
  /// /////// Animation ///////
  /// /////////////////////////
  // Reveal cards stage-by-stage (left to right), then draw connectors.
  // All animation is driven by per-element `--delay` CSS custom properties
  // and pure CSS keyframes — no JS frame loop.
  const ANIM = {
    CARD_DURATION: 350, // ms — single card fade/slide
    CARD_STAGGER: 35, // ms — between cards within a round
    ROUND_DELAY: 200, // ms — between rounds
    CONNECTOR_DURATION: 200, // ms — line draw
    CONNECTOR_OFFSET: 200, // ms — after round's cards start
  };

  const cardDelay = (roundIdx: number, matchIdx: number) =>
    roundIdx * ANIM.ROUND_DELAY + matchIdx * ANIM.CARD_STAGGER;

  const connectorDelay = (roundIdx: number) =>
    roundIdx * ANIM.ROUND_DELAY + ANIM.CONNECTOR_OFFSET;

  // The third-place match lives outside the main grid; show it with the
  // final round.
  let thirdPlaceDelay = $derived((numRounds - 1) * ANIM.ROUND_DELAY);

  // Trigger reveal once when the bracket scrolls into view.
  let wrapperEl = $state<HTMLElement | undefined>();
  let animateIn = $state(false);

  $effect(() => {
    if (!wrapperEl) return;
    // Respect reduced motion — show final state immediately.
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      animateIn = true;
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      animateIn = true;
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            animateIn = true;
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.05 }
    );
    io.observe(wrapperEl);
    return () => io.disconnect();
  });
</script>

{#if appState.isLoading}
  <Spinner />
{/if}

{#if hasBracketData}
  <GraphicBlock width="fluid" notes={note} id="bracket">
    <div
      class="wrapper"
      class:animate-in={animateIn}
      aria-hidden="true"
      bind:clientWidth={widthContainer}
      bind:this={wrapperEl}
      style="--h:{CARD_HEIGHT}; --card-duration:{ANIM.CARD_DURATION}ms; --connector-duration:{ANIM.CONNECTOR_DURATION}ms;"
      style:height={`${heightContainer}px`}
    >
      {#if widthContainer}
        <!-- Path connecting each match to next stage -->
        <svg {width} {height}>
          {#each connectorRounds as round, roundIdx (round.round)}
            <g transform="translate({round.xPos}, {round.yPos + MARGINS.top})">
              {#each round.matches as match, i (i)}
                <path
                  class="connector"
                  pathLength="1"
                  style:--delay={`${connectorDelay(roundIdx)}ms`}
                  d={drawElbow(
                    match.sourceX,
                    match.sourceY,
                    match.targetX,
                    match.targetY
                  )}
                ></path>
              {/each}
            </g>
          {/each}
        </svg>

        <!-- Match container and info -->
        <div
          class="team-boxes-container"
          style:width={`${width}px`}
          style:top={`${MARGINS.top}px`}
          style:height={`${boundedHeight}px`}
        >
          <!-- Labels for the rounds on top -->
          <div class="round-labels-container">
            {#each roundsPositions as roundPos, roundIdx (roundPos.round)}
              <div
                class="round-label"
                style:position="absolute"
                style:left={`${roundPos.x}px`}
                style:top={`${roundPos.y}px`}
                style:--delay={`${cardDelay(roundIdx, 0)}ms`}
              >
                {roundPos.round}
              </div>
            {/each}
          </div>

          <!-- Third place match -->
          {#if thirdPlaceMatch?.matches?.[0]}
            <div
              class="third-place-container"
              style:right={`${MARGINS.right}px`}
              style:--delay={`${thirdPlaceDelay}ms`}
            >
              <div class="round-label">{thirdPlaceMatch.round}</div>
              <div style:width={`${widthRect}px`}>
                <MatchEntry
                  match={thirdPlaceMatch.matches[0]}
                  round={thirdPlaceMatch.round}
                  {widthRect}
                  {heightRect}
                  {venues}
                />
              </div>
            </div>
          {/if}

          <!-- All other matches -->
          {#each cardRounds as round, roundIdx (round.round)}
            <div class="round-container" style:top={`${round.yPos}px`}>
              {#each round.matches as match, i (i)}
                <div
                  class="match-container"
                  style:left={`${match.x}px`}
                  style:top={`${match.y}px`}
                  style:--delay={`${cardDelay(roundIdx, i)}ms`}
                >
                  <MatchEntry
                    {match}
                    round={round.round}
                    {widthRect}
                    {heightRect}
                    {venues}
                  />
                </div>
              {/each}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </GraphicBlock>
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  .wrapper {
    position: relative;
    overflow-x: auto;
    overflow-y: hidden;
  }

  path.connector {
    stroke-width: 2px;
    fill: none;
    stroke: var(--theme-colour-tan-grey);
  }

  .round-labels-container {
    position: absolute;
    width: 100%;
    z-index: 1;
  }

  .team-boxes-container {
    position: absolute;
    top: 0;
    left: 0;
  }

  .round-container,
  .match-container {
    position: absolute;
  }

  .third-place-container {
    position: absolute;
    bottom: 0;
    // `right` is set inline from `MARGINS.right` so the third-place block
    // respects the same horizontal gutter as the bracket cards.
  }

  .round-label {
    position: absolute;
    @include mixins.h4;
    @include mixins.text-secondary;
    transform: translateY(calc(var(--h) * -0.35px));
  }

  /* ----- Reveal animation -----
     Cards slide in from the left, staggered round-by-round. Connector
     paths draw in just after their round's cards start appearing. */
  @media (prefers-reduced-motion: no-preference) {
    /* Pre-animation state: hide everything that will animate. */
    .wrapper:not(.animate-in) {
      .match-container,
      .third-place-container,
      .round-label {
        opacity: 0;
        transform: translateX(-14px);
      }
      /* Keep round-label's existing Y offset while hidden. */
      .round-label {
        transform: translate(-14px, calc(var(--h) * -0.35px));
      }
      path.connector {
        stroke-dasharray: 1;
        stroke-dashoffset: 1;
      }
    }

    .wrapper.animate-in {
      .match-container,
      .third-place-container {
        animation: bracketCardIn var(--card-duration, 260ms)
          cubic-bezier(0.22, 0.61, 0.36, 1) both;
        animation-delay: var(--delay, 0ms);
      }
      .round-label {
        animation: bracketLabelIn var(--card-duration, 260ms)
          cubic-bezier(0.22, 0.61, 0.36, 1) both;
        animation-delay: var(--delay, 0ms);
      }
      path.connector {
        stroke-dasharray: 1;
        stroke-dashoffset: 1;
        animation: bracketLineDraw var(--connector-duration, 360ms) ease-out
          both;
        animation-delay: var(--delay, 0ms);
      }
    }
  }

  @keyframes bracketCardIn {
    from {
      opacity: 0;
      transform: translateX(-14px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes bracketLabelIn {
    from {
      opacity: 0;
      transform: translate(-14px, calc(var(--h) * -0.35px));
    }
    to {
      opacity: 1;
      transform: translate(0, calc(var(--h) * -0.35px));
    }
  }

  @keyframes bracketLineDraw {
    to {
      stroke-dashoffset: 0;
    }
  }
</style>
